import ActionButton from '@jimmyboys/liita-ui/dist/components/ActionButton'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import TaskHeaderErrors from 'components/TaskHeaderErrors/TaskHeaderErrors'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import styles from './../../Dialog.module.sass'
import VStack from '@jimmyboys/liita-ui/dist/components/VStack'
import HFTextInput from 'components/HFInputs/HFTextInput'
import HFDatepicker from 'components/HFInputs/HFDatepicker'
import HFOptionGroup from 'components/HFInputs/HFOptionGroup'
import { booleanOptions } from 'controllers/TaskManager/formOptions'
import HFDropdown from 'components/HFInputs/HFDropdown'
import { useFetchList } from 'store'
import { useDeepCompareEffect } from 'react-use'
import { zodResolver } from '@hookform/resolvers/zod'
import { EditProcessDataSchema } from './EditProcessDataSchema'
import { flatHookFormErrors } from 'controllers/TaskManager/controllers/utils'
import { API_URL, fetchOptions, parseJSONContent } from 'store/fetchUtils'
import { translateErrorMessage } from 'hooks/useSubmitTask'
import useSession from 'hooks/useSession'
import moment from 'moment'
import HFMultiSelect from '../../../components/HFInputs/HFMultipleSelect'
const { Options } = require('models')

const managementType = {
	TRT: 'Trust',
	SEG: 'Seguradora',
	PRE: 'Prestador',
	MT: 'Medicina T.',
}

const EditProcessDataDialog = (props) => {
	const { data, dismiss, resolve } = props
	const { process } = data
	const { i18n, pushNotification } = useGlobal()
	const { logout } = useSession()
	const [defaultValues, setDefaultValues] = useState(null)
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState(null)

	const isSportsAccident = process.accident?.segment?.abbreviation === 'AD'
	const isAviationAccident = process.accident?.segment?.abbreviation === 'AV'

	const methods = useForm({
		mode: 'onSubmit',
		resolver: zodResolver(
			EditProcessDataSchema(isSportsAccident || isAviationAccident)
		),
		shouldUnregister: true,
	})

	const { handleSubmit, watch, formState, reset } = methods

	const [isPensioner] = watch(['isPensioner'])

	const managementTypesUri = '/metadata/management_type_types'
	const accidentSitesUri = '/metadata/accident_sites'
	const sportAccidentSitesUri = '/metadata/sport_accident_sites'
	const processTagsUri = '/metadata/tags'
	const claimsManagerUri = `/clinical_processes/${process?.id}/claims_managers`

	const {
		contents: [
			managementTypes,
			accidentSites,
			sportAccidentSites,
			claimsManager,
			processTags,
		],
	} = useFetchList(
		[
			managementTypesUri,
			accidentSitesUri,
			sportAccidentSitesUri,
			claimsManagerUri,
			processTagsUri,
		],
		[
			(json) => {
				return {
					value: json.id.toString(),
					text: managementType[json.name],
				}
			},
			Options.fromSimpleObjectJSON,
			Options.fromSimpleObjectJSON,
			Options.fromSimpleObjectJSON,
			(json) => {
				return {
					value: json.id.toString(),
					text: i18n.translate(json.name),
				}
			},
		]
	)

	const processTagOptions = [
		{ value: '', text: i18n`No tag` },
		...(processTags || []),
	]

	const onSubmit = (formData) => {
		if (submitting) {
			return
		}

		const endpoint = `${API_URL}/api/clinical_processes/${process?.id}`

		setError(null)
		setSubmitting('submitting')

		return fetch(
			endpoint,
			fetchOptions({
				method: 'PUT',
				body: formData,
			})
		)
			.then((r) => parseJSONContent(r))
			.then(() => {
				resolve && resolve()
				pushNotification(
					undefined,
					'success',
					i18n`Process updated successfully!`
				)
			})
			.catch((e) => {
				if (e.status === '401' || e.status === 401) {
					logout()
				}

				const errorMessage = translateErrorMessage(e, i18n)

				setSubmitting(false)
				setError([errorMessage])
			})
	}

	useEffect(() => {
		if (
			(data.formState || defaultValues) &&
			!formState.isDirty &&
			formState.submitCount === 0
		) {
			reset && reset(data.formState ? data.formState : defaultValues)
		}
	}, [
		reset,
		defaultValues,
		data.formState,
		formState.isDirty,
		formState.submitCount,
	])

	useDeepCompareEffect(() => {
		let defaultValues = {}

		if (process?.info) {
			defaultValues = {
				...defaultValues,
				admissionDate: process.info.admissionDate?.date?.toISOString(),
				biologicalRisk: process.info.biologicalRisk,
				managementType: process.info?.managementType
					? process.info.managementType
					: 'TRT',
				administrativeTransport: process.info.admTransportApproval,
			}
		}

		if (process?.accident) {
			defaultValues = {
				...defaultValues,
				accidentDate: process.accident?.date?.date
					? process.accident.date.date.toISOString()
					: undefined,
				pensionerDate: process.accident?.pensionerDate?.date
					? process.accident.pensionerDate.date.toISOString()
					: new Date().toISOString(),
				isPensioner: process.accident?.isPensioner
					? process.accident.isPensioner
					: false,
				accidentSite: process?.accident?.localId
					? `${process.accident.localId}`
					: undefined,
				accidentDescription: process.accident.description,
			}
		}

		if (process?.insurancePolicy?.externalId) {
			defaultValues = {
				...defaultValues,
				insuranceNumber: process.insurancePolicy.externalId,
			}
		}

		if (process?.companyClaimsManager?.id) {
			defaultValues = {
				...defaultValues,
				claimsManager: process?.companyClaimsManager?.id,
			}
		}

		defaultValues = {
			...defaultValues,
			processTags:
				process.processTags?.map((t) => t.tagId.toString()) ?? [],
		}
		setDefaultValues({ ...defaultValues })
	}, [process ?? {}])

	useEffect(() => {
		const flattenErrors = flatHookFormErrors(formState.errors)

		if (flattenErrors) {
			const errorMessages = Object.keys(flattenErrors).map((errorKey) => {
				const errorMessage = flattenErrors[errorKey]
				const splittedErrorKey = errorKey.split('.')
				const field = splittedErrorKey[splittedErrorKey.length - 2]

				return `${i18n.translate(field)}: ${i18n.translate(
					errorMessage
				)}`
			})
			setError(errorMessages)
		}
	}, [formState.errors, i18n])

	return (
		<>
			<TaskHeaderErrors errors={error ?? []} />
			<FormProvider {...methods}>
				<form
					id={'form_process_data_edit'}
					onSubmit={handleSubmit(onSubmit)}
					className={`${styles['data-editing-form'] || ''}`}
					disabled={submitting}
				>
					<div className={styles['form-input-group']}>
						<div style={{ gridColumn: '1 / -1' }}>
							<VStack>
								<h5>{i18n`Process tags`}</h5>
								<HFMultiSelect
									name='processTags'
									noData={i18n`No results`}
									options={processTags || []}
								/>
							</VStack>
						</div>
						<VStack>
							<h5>{i18n`Admission date`}</h5>
							<HFDatepicker
								name='admissionDate'
								type='datetime-local'
								initialDate={process.info?.admissionDate?.date}
							/>
						</VStack>
						<VStack>
							<h5>{i18n`Accident date`}</h5>
							<HFDatepicker
								name='accidentDate'
								type='datetime-local'
								initialDate={process.accident?.date?.date}
							/>
						</VStack>
						<VStack>
							<h5>{i18n`Insurance process number`}</h5>
							<HFTextInput name='insuranceNumber' />
						</VStack>
						<VStack>
							<h5>{i18n`Biological risk`}</h5>
							<HFOptionGroup
								className={`${styles['edit-option-group']}`}
								name='biologicalRisk'
								type='boolean'
								options={booleanOptions}
							/>
						</VStack>
						<VStack>
							<h5>{i18n`Pensioner`}</h5>
							<HFOptionGroup
								className={`${styles['edit-option-group']}`}
								name='isPensioner'
								type='boolean'
								options={booleanOptions}
							/>
						</VStack>
						{isPensioner ? (
							<VStack>
								<h5>{i18n`Pensioner date`}</h5>
								<HFDatepicker
									name='pensionerDate'
									type='date'
									initialDate={
										process.accident?.pensionerDate?.date ||
										moment(new Date())
									}
								/>
							</VStack>
						) : (
							<div />
						)}
						<VStack>
							<h5>{i18n`Management Type`}</h5>
							<HFDropdown
								optionsAlign='right'
								searchable={true}
								name='managementType'
								noData={i18n`No results`}
								options={managementTypes || []}
								type='number'
							/>
						</VStack>
						<VStack>
							<h5>{i18n`Insurance manager`}</h5>
							<HFDropdown
								optionsAlign='right'
								searchable={true}
								name='claimsManager'
								noData={i18n`No results`}
								options={claimsManager || []}
								type='number'
							/>
						</VStack>
						{isSportsAccident || isAviationAccident ? (
							<VStack>
								<h5>{i18n`Accident site`}</h5>
								<HFDropdown
									optionsAlign='right'
									searchable={true}
									name='accidentSite'
									noData={i18n`No results`}
									options={
										isSportsAccident
											? sportAccidentSites
											: accidentSites
									}
									type='number'
								/>
							</VStack>
						) : null}
						<VStack>
							<h5>{i18n`Allow administrative transport`}</h5>
							<HFOptionGroup
								className={`${styles['edit-option-group']}`}
								name='administrativeTransport'
								type='boolean'
								options={booleanOptions}
							/>
						</VStack>

						<VStack
							className={`${
								styles['data-editing-accident-description'] ||
								''
							}`}
						>
							<h5>{i18n`Accident description`}</h5>
							<HFTextInput
								name='accidentDescription'
								placeholder={i18n`Accident description`}
								multiline={true}
								rows={5}
								noResize
							/>
						</VStack>
					</div>
				</form>
			</FormProvider>

			<menu>
				<ActionButton type='button' secondary onClick={dismiss}>
					<small>
						<strong>{i18n`Cancel`}</strong>
					</small>
				</ActionButton>
				<ActionButton form={`form_process_data_edit`} primary>
					<small>
						<strong>
							{submitting ? i18n`Submitting` : i18n`Save`}
						</strong>
					</small>
				</ActionButton>
			</menu>
		</>
	)
}

export default EditProcessDataDialog
